import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  Divider,
  IconButton,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Close, FiberManualRecord } from '@material-ui/icons';
import Excel from 'assets/icons/svg/Excel';
import { IServiceType } from '../../interfaces';
import { GoldCupIcon, BronzeCupIcon, SilverCupIcon, MedalIcon } from '../../assets/icons';
import { V1_EFFECTIVE_TO, V2_EFFECTIVE_FROM } from '../../constants';
import { useGetCriteriaRatings, useGetPartnerRatingByPartner, useGetPartnerRatingByPartnerV2 } from '../../hooks';
import PartnerRatingTableRow from './v1/PartnerRatingTableRow';
import V1ColGroup from './v1/PartnerRatingColGroup';
import PartnerRatingTable from './v2/PartnerRatingTable';
import { useBorderedTableStyles } from './borderedTable.styles';

// Версия методики рейтинга: 2 — новая (по умолчанию), 1 — прежняя.
const RATING_VERSION = { V2: 2, V1: 1 } as const;

const STATUS_BOX_MIN_HEIGHT = 240;

interface IProps extends Omit<DialogProps, 'onClose'> {
  partnerGuid: string;
  partnerName: string;
  serviceType?: IServiceType;
  year: number;
  partnersCount: number;
  isActivePartner: boolean;
  exportExcelLoading: boolean;
  onExportExcel(): void;
  onClose(): void;
}

const PartnerRatingDialog: React.FC<IProps> = ({
  open,
  partnerGuid,
  partnerName,
  serviceType,
  year,
  partnersCount,
  isActivePartner,
  exportExcelLoading,
  onExportExcel,
  onClose,
  ...props
}) => {
  const [version, setVersion] = useState<number>(RATING_VERSION.V2);
  const isV2 = version === RATING_VERSION.V2;

  const borderedTableClasses = useBorderedTableStyles();

  // Данные грузятся по активной вкладке: v2 — сразу при открытии, v1 + criteria — лениво при переходе на «1.0».
  const v2 = useGetPartnerRatingByPartnerV2();
  const v1 = useGetPartnerRatingByPartner();
  const criteria = useGetCriteriaRatings(isV2 ? undefined : serviceType?.serviceTypeId);

  const params = { serviceTypeId: serviceType?.serviceTypeId, year };
  const mapKey = Object.values({ partnerGuid, ...params })
    .filter(Boolean)
    .join('-');

  const v2Data = v2.data.get(mapKey) || null;
  const v1Data = v1.data.get(mapKey) || null;

  useEffect(() => {
    if (!open || !partnerGuid) return;
    if (isV2) v2.fetchPartnerRatingByPartnerV2(partnerGuid, { params });
    else v1.fetchPartnerRatingByPartner(partnerGuid, { params });
  }, [open, version, partnerGuid, serviceType?.serviceTypeId, year]); // eslint-disable-line react-hooks/exhaustive-deps

  // Сброс на вкладку v2 при закрытии — следующее открытие всегда начинается с новой методики.
  useEffect(() => {
    if (!open) setVersion(RATING_VERSION.V2);
  }, [open]);

  const activeData = isV2 ? v2Data : v1Data;
  const activeError = isV2 ? v2.error : v1.error;
  const activeReady = isV2 ? v2.success : v1.success && !criteria.loading;
  const ratingPosition = activeData?.ratingPosition;

  const renderStatusBox = (content: React.ReactNode) => (
    <Box
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}
      style={{ minHeight: STATUS_BOX_MIN_HEIGHT }}
    >
      {content}
    </Box>
  );

  const renderCaption = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gridGap: 8, py: 2 }}>
      <FiberManualRecord color="primary" style={{ fontSize: 12 }} />
      <Typography component="span" variant="body2" color="textSecondary">
        {isV2 ? `Действуют с ${V2_EFFECTIVE_FROM}` : `Период до ${V1_EFFECTIVE_TO}`}
      </Typography>
    </Box>
  );

  const renderBody = () => {
    if (activeError) {
      return renderStatusBox(
        <Typography component="h3" variant="h4" align="center">
          {activeError}
        </Typography>,
      );
    }

    if (!activeReady) return renderStatusBox(<CircularProgress />);

    if (isV2) {
      return v2Data ? (
        <PartnerRatingTable data={v2Data} year={year} serviceType={serviceType} />
      ) : (
        renderStatusBox(
          <Typography component="h3" variant="h4" align="center">
            Данные отсутствуют
          </Typography>,
        )
      );
    }

    if (!v1Data) {
      return renderStatusBox(
        <Typography component="h3" variant="h4" align="center">
          Данные отсутствуют
        </Typography>,
      );
    }

    return (
      <TableContainer style={{ width: 'calc(100% + 48px)', margin: '0 -24px', maxHeight: 500, overflow: 'auto' }}>
        <Table stickyHeader size="small" className={borderedTableClasses.table} style={{ tableLayout: 'fixed' }}>
          <V1ColGroup criteriaCount={criteria.data.length} />

          <TableHead>
            <TableRow>
              <TableCell>
                <b>ЖК</b>
              </TableCell>
              <TableCell>
                <b>Благодарности от жителей</b>
              </TableCell>
              <TableCell>
                <b>Благодарности от ОВКС</b>
              </TableCell>
              <TableCell>
                <b>Жалобы от жителей</b>
              </TableCell>
              <TableCell>
                <b>Протокола от ОВКС</b>
              </TableCell>
              <TableCell>
                <b>Замечания от жителей</b>
              </TableCell>
              <TableCell>
                <b>Акты ОВКС</b>
              </TableCell>

              {criteria.data.map((criteriaRating) => (
                <TableCell key={criteriaRating.criteriaRatingId}>{criteriaRating.criteriaRatingName}</TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {v1Data.realEstates.map((realEstateRating) => (
              <PartnerRatingTableRow
                key={realEstateRating.realEstateGuid}
                year={year}
                serviceType={serviceType}
                partnerRating={v1Data}
                realEstateRating={realEstateRating}
                criteriaRatingsByServiceType={criteria.data}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog {...props} open={open} onClose={onClose} maxWidth="xl" fullWidth scroll="paper">
      <AppBar style={{ position: 'relative' }}>
        <Box component={Toolbar} sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography component="h3" variant="h5">
            <b>Рейтинг партнеров</b>
          </Typography>

          <IconButton aria-label="Закрыть" color="inherit" onClick={onClose}>
            <Close color="inherit" />
          </IconButton>
        </Box>
      </AppBar>

      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography style={{ maxWidth: '70%' }} component="h2" variant="h3">
              <b>{partnerName}</b>
            </Typography>

            {serviceType && (
              <Typography component="p" variant="h6">
                {serviceType.serviceTypeName}
              </Typography>
            )}

            <Typography component="p" variant="h6">
              БИН: {activeData?.partnerBin}
            </Typography>

            <Typography component="p" variant="h6">
              {activeData?.partnerDirector}
            </Typography>

            <Typography component="p" variant="h6">
              {activeData?.partnerPhone}
            </Typography>
          </Box>

          {isActivePartner && Boolean(ratingPosition) && (
            <Box sx={{ display: 'flex', gridGap: 16 }}>
              <Box>
                <Typography component="h3" variant="h3" noWrap>
                  <b>{ratingPosition} место</b>
                </Typography>

                <Typography component="h4" variant="h6">
                  Всего партнеров {partnersCount}
                </Typography>
              </Box>

              {[1, 2, 3].some((position) => position === ratingPosition) ? (
                ratingPosition === 1 ? (
                  <GoldCupIcon />
                ) : ratingPosition === 2 ? (
                  <SilverCupIcon />
                ) : (
                  <BronzeCupIcon />
                )
              ) : [4, 5, 6, 7, 8, 9, 10].some((position) => position === ratingPosition) ? (
                <MedalIcon />
              ) : null}
            </Box>
          )}
        </Box>

        <Box style={{ width: 'calc(100% + 48px)', margin: '0 -24px' }}>
          <Tabs
            value={version}
            onChange={(_, value) => setVersion(value)}
            indicatorColor="primary"
            textColor="primary"
            style={{ paddingLeft: 24, paddingRight: 24 }}
          >
            <Tab label="Рейтинг 2.0" value={RATING_VERSION.V2} />
            <Tab label="Рейтинг 1.0" value={RATING_VERSION.V1} />
          </Tabs>
          <Divider />
        </Box>

        {renderCaption()}

        {renderBody()}
      </DialogContent>

      <DialogActions>
        <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Tooltip title="Скачать отчет в Excel">
            <span>
              <IconButton
                aria-label="Скачать отчет в Excel"
                onClick={onExportExcel}
                disabled={!activeReady || exportExcelLoading}
              >
                <Excel style={{ color: '#3C763D' }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerRatingDialog;
